// OperatorTable.js
import React, { useEffect, useState } from "react";
import { Container, Row, Col, Table, Button, Modal } from "react-bootstrap";
import { collection, getDocs, query, orderBy, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import Filter from "./Filter";
import EditOperatorModal from "./EditOperatorModal";
import PaginationControl from "../calculations/PaginationControl";

const OperatorTable = () => {
    const [operators, setOperators] = useState([]);
    const [order, setOrder] = useState("desc");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const [showEditOperatorModal, setShowEditOperatorModal] = useState(false);
    const [currentOperator, setCurrentOperator] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteOperatorId, setDeleteOperatorId] = useState(null);

    const fetchOperators = async (sortOrder = "asc") => {
        try {
            const q = query(collection(db, "operators"), orderBy("operatorId", sortOrder));
            const snapshot = await getDocs(q);
            const data = snapshot.docs.map((doc) => ({ docId: doc.id, ...doc.data() }));
            setOperators(data);
        } catch (error) {
            console.error("Error fetching operators:", error);
        }
    };

    useEffect(() => {
        fetchOperators(order);
    }, [order]);

    const handleFilterChange = (e) => {
        setOrder(e.target.value);
        setCurrentPage(1);
    };

    const handleEditClick = (operator) => {
        setCurrentOperator(operator);
        setShowEditOperatorModal(true);
    };

    const handleDeleteClick = (docId) => {
        setDeleteOperatorId(docId);
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteDoc(doc(db, "operators", deleteOperatorId));
            setOperators((prev) => prev.filter((op) => op.docId !== deleteOperatorId));
            setShowDeleteConfirm(false);
        } catch (error) {
            console.error("Error deleting operator:", error);
        }
    };

    const handleEditSave = async () => {
        try {
            const { docId, operatorId, operatorName, country, code } = currentOperator;
            const operatorRef = doc(db, "operators", docId);
            await updateDoc(operatorRef, { operatorId, operatorName, country, code });
            setOperators((prev) => prev.map((op) => (op.docId === docId ? currentOperator : op)));
            setShowEditOperatorModal(false);
        } catch (error) {
            console.error("Error updating operator:", error);
        }
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentOperators = operators.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(operators.length / itemsPerPage);

    return (
        <section className="px-1 py-4 nav-body-bg btm-rad">
            <Container>
                <Row className="align-items-center justify-content-between flex-column flex-md-row text-center text-md-start">
                    <Col xs="auto" className="mb-3 mb-md-0">
                        <h3 className="text-h2">All Operators</h3>
                    </Col>
                    <Col xs="auto">
                        <Filter value={order} onChange={handleFilterChange} />
                    </Col>
                </Row>
                <div className="table-responsive scrollable-table-y mt-4 bg-transparent" style={{ minHeight: '100vh', overflowY: 'auto' }}>
                    <style>{`
                        @media (max-width: 768px) {
                            .scrollable-table-y { max-height: 300px; overflow-y: auto; }
                        }
                    `}</style>
                    <Table className="table-hover align-middle body-text-two mb-0">
                        <thead className="sticky-top top-0" style={{ zIndex: 10, background: 'transparent !important' }}>
                            <tr>
                                <th className="transparent-header">SL</th>
                                <th className="text-center transparent-header">Operator Name</th>
                                <th className="text-center transparent-header">Country</th>
                                <th className="text-center transparent-header">Code</th>
                                <th className="text-center transparent-header">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentOperators.map((operator, index) => (
                                <tr key={index} style={{ borderBottom: '1px solid #8CB6EF' }}>
                                    <td className="py-3 transparent-header">{index + 1}</td>
                                    <td className="py-3 transparent-header text-center">{operator.operatorName}</td>
                                    <td className="py-3 transparent-header text-center">{operator.country}</td>
                                    <td className="py-3 transparent-header text-center">{operator.code}</td>
                                    <td className="text-center py-3 transparent-header">
                                        <Button variant="link" className="text-success me-3 p-0" onClick={() => handleEditClick(operator)}>Edit</Button>
                                        <Button variant="link" className="text-danger p-0" onClick={() => handleDeleteClick(operator.docId)}>Delete</Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
                <div className="d-flex justify-content-center mt-4">
                    {/* Pagination */}
                    <PaginationControl totalPages={totalPages} currentPage={currentPage} setCurrentPage={setCurrentPage} />
                </div>
            </Container>
            <EditOperatorModal show={showEditOperatorModal} onHide={() => setShowEditOperatorModal(false)} operator={currentOperator} onChange={setCurrentOperator} onSave={handleEditSave} />
            <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>Are you sure you want to delete this operator?</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
                    <Button variant="danger" onClick={handleDeleteConfirm}>Delete</Button>
                </Modal.Footer>
            </Modal>
        </section>
    );
};

export default OperatorTable;